import React, { useState } from "react";
import SearchIcon from "../../../assets/svg/search-icon";
import CheckIcon from "../../../assets/svg/check-icon";
import Button from "../../Button/Button";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import {
  setCommunicationsUtmSources,
  setCrmLeadsUtmSources,
} from "../../../redux/slices/filterSlice";
import { useGetListingFiltersQuery } from "../../../redux/services/communicationApi";

const UtmSourcesFilter = ({ onClose, isCrm = false}: { onClose: () => void; isCrm?: boolean;}) => {
  const selectedUtms = useSelector((state: RootState) =>
    isCrm
      ? state.filter.crmLeads.utmSources
      : state.filter.communications.utmSources
  );
  const duration = useSelector((state: RootState) =>
    isCrm
      ? state.filter.crmLeads.duration
      : state.filter.communications.duration
  );
  const selectedChatType = useSelector(
    (state: RootState) => state.leads.selectedChatType
  );
  const {
    data: listingFilters,
    isLoading,
    isFetching,
  } = useGetListingFiltersQuery({
    start_date: duration.startDate,
    end_date: duration.endDate,
    ...(isCrm ? {} : { chat_type: selectedChatType as string }),
  });
  const dispatch = useDispatch();
  const [selectedUtmSources, setSelectedUtmSources] = useState<string[]>(
    selectedUtms.value as string[]
  );
  const [searchQuery, setSearchQuery] = useState("");

  const utmSources: string[] = listingFilters?.utm_sources || [];
  const filteredUtmSources = utmSources.filter((utmSource) =>
    utmSource.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const handleUtmSourceSelect = (utmSource: string) => {
    setSelectedUtmSources((prevUtmSources) => {
      if (prevUtmSources.includes(utmSource)) {
        return prevUtmSources.filter((source) => source !== utmSource);
      }
      return [...prevUtmSources, utmSource];
    });
  };

  const handleCancel = () => {
    setSelectedUtmSources([]);
    setSearchQuery("");
    onClose();
  };

  const handleApply = () => {
    dispatch(
      (isCrm ? setCrmLeadsUtmSources : setCommunicationsUtmSources)({
        value: selectedUtmSources,
        displayValue: selectedUtmSources,
      })
    );
    onClose();
  };
  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-70px)]">
      {/* Search Input */}
      <div className="relative border-b border-border-gray dark:border-border-gray-dk">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search UTM Sources"
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>

      <span className="text-xxs font-semibold text-data dark:text-data-dk rounded-sm px-3 py-1">
        SELECTED ({selectedUtmSources?.length ?? 0})
      </span>

      <div className="overflow-y-auto">
        {/* Selected Properties Display */}
        {selectedUtmSources.length > 0 && (
          <div className="flex flex-col divide-y divide-border-gray dark:divide-border-gray-dk">
            {selectedUtmSources.map((utmSource) => (
              <div
                key={utmSource}
                onClick={() => handleUtmSourceSelect(utmSource)}
                className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                    {utmSource}
                  </span>
                </div>
                <div className="flex items-center justify-center w-3 h-3 border rounded border-verba-blue dark:border-verba-blue-dk bg-verba-blue dark:bg-verba-blue-dk">
                  <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Properties List */}
        <div className="flex flex-col">
          <div className="px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
            All Utm Sources
          </div>

          <div className="overflow-y-auto">
            {isLoading || isFetching ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                  {isLoading
                    ? "Loading utm sources..."
                    : "Fetching utm sources..."}
                </div>
              </div>
            ) : filteredUtmSources.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                  {searchQuery.trim()
                    ? "No utm sources found"
                    : "No utm sources available"}
                </div>
              </div>
            ) : (
              filteredUtmSources
                ?.filter(
                  (utmSource: string) => !selectedUtmSources.includes(utmSource)
                )
                ?.map((utmSource: string) => (
                  <div
                    key={utmSource}
                    onClick={() => handleUtmSourceSelect(utmSource)}
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                        {utmSource}
                      </div>
                    </div>
                    <div className="flex items-center justify-center w-3 h-3 border rounded border-border-gray dark:border-border-gray-dk">
                      <div className="w-3 h-3 rounded-sm bg-transparent" />
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-6 px-3">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={handleCancel}
        />
        <Button
          innerText="Apply"
          disabled={selectedUtmSources.length === 0}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default UtmSourcesFilter;
